import { Box } from "@mui/material";
import { differenceInMonths } from "date-fns";
import { FormikProvider, useFormik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LocalStorageKey } from "../../../constants/localStorageKeys";
import { FormHelper } from "../../../helpers/formHelper";
import { trackSurveySubmitted } from "../../../helpers/surveyTrackingHelper";
import { PortfolioService } from "../../../pages/investor/views/portfolio/portfolioService";
import { LanguageService } from "../../../services/languageService";
import { UserService } from "../../../services/userService";
import { DialogWrapper } from "../common/dialogWrapper";
import { FsButton } from "../button";
import { InputWrapper } from "../formUtilities/useInput";
import { TextWrapper } from "../formUtilities/useTextWrapper";

export const UserSurveyDialog = () => {
  const languageService = new LanguageService();
  const userService = new UserService();
  const portfolioService = new PortfolioService();
  const [open, setOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState(0);
  const surveyPoints = Array.from({ length: 10 }, (_, i) => i + 1);
  const { t } = useTranslation();

  const form = useFormik({
    initialValues: { rating: 0, suggestion: "" },
    onSubmit: () => {
      onSubmit(true);
    },
    ...FormHelper.formikProps()
  });

  useEffect(() => {
    checkIfUserLoggedInFirst();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getLenderSummary = (lastSurveyDate = null) => {
    let storedOrders = localStorage.getItem(LocalStorageKey.myOrders) ? JSON.parse(localStorage.getItem(LocalStorageKey.myOrders)) : [];
    if (storedOrders && storedOrders?.length) {
      checkLastSurvey(lastSurveyDate, storedOrders);
      return;
    }
    portfolioService.getOrders().then(resp => {
      let orders = resp.data || [];
      localStorage.setItem(LocalStorageKey.myOrders, JSON.stringify(orders));
      checkLastSurvey(lastSurveyDate, orders);

    });
  };

  const checkLastSurvey = (lastSurveyDate, orders) => {
    if (orders?.length) {
      if (!lastSurveyDate) {
        openDialog();
        return;
      }
    }
    if (lastSurveyDate) {
      let isMorethan3Months = Math.abs(differenceInMonths(new Date(lastSurveyDate), new Date()));
      if (isMorethan3Months > 2) {
        openDialog();
        return;
      }
    }
  };

  const checkIfUserLoggedInFirst = async () => {
    const checkPointData = await userService.getCheckPoint();
    if (checkPointData) {
      localStorage.setItem(LocalStorageKey.checkPointServey, JSON.stringify(checkPointData.data));
    }
    const lastSurveyDateData = localStorage.getItem(LocalStorageKey.checkPointServey) ? JSON.parse(localStorage.getItem(LocalStorageKey.checkPointServey)) : null;
    if (lastSurveyDateData) {
      getLenderSummary(lastSurveyDateData?.lastSurveyDate);
    }
  };

  const openDialog = () => {
    setTimeout(() => {
      setOpen(true);
    }, 1000);
  };


  const onSubmit = (isSubmit = true) => {
    if (isSuccess) {
      setOpen(false);
      return;
    }
    setLoading(true);
    let formValue = form.values;
    formValue.rating = selectedPoint;
    userService.getAddSurvey(formValue).then(resp => {
      setLoading(false);
      if (isSubmit) {
        // Explicit submits only — dismiss-close also saves the survey but is
        // not a real response (rating may be 0), so it must not be tracked.
        trackSurveySubmitted(formValue.rating, formValue.suggestion);
        setIsSuccess(true);
      } else {
        setOpen(false);
      }
    }).catch(err => {
      setLoading(false);
    });
  };

  const isAr = () => {
    return languageService.isArabic();
  };

  return <DialogWrapper
    maxWidth="xs"
    state={open}
    preventOutsideClick={true}
    onClose={() => {
      onSubmit(false);
    }}
    title={!isSuccess ? t('howLikelyAreYouToRecommend') : ''}
    subTitle={!isSuccess ? t('yourOptionIsMatters') : ''}
    content={
      <>{!isSuccess ? <Box className="flex flex-col relative space-y-3 mt-2">
        <TextWrapper
          classList={`!font-bold !text-start !text-[14px]`}
          text={t('howLikelyRefer')}></TextWrapper>
        <Box className='flex flex-row items-center justify-between'>
          {surveyPoints.map((resp, index) => {
            return <Box onClick={() => {
              setSelectedPoint(resp);
            }} key={index} className={`border border-[#ccc] survey-points !text-[13px] font-[500] ${isAr() ? 'md:ml-0 ml-[4px]' : 'md:mr-0 mr-[4px]'} cursor-pointer rounded-full flex justify-center items-center h-[34px] w-[34px] font-[500] ${selectedPoint === resp ? 'active' : ''}`}>
              {resp}
            </Box>;
          })}
        </Box>
        <Box className='flex justify-between items-center relative -top-1'>
          <TextWrapper classList="!caption !text-[11px] font-[600]" text={t('notLikely')} />
          <TextWrapper classList="!caption !text-[11px] font-[600]" text={t('veryLikely')} />
        </Box>
        <Box className='flex flex-col !mt-3'>
          <TextWrapper
            classList={`!font-bold !text-start !text-[14px]`}
            text={t('whatCanWeDoBetter')}></TextWrapper>
          <Box className='mt-2'>
            <FormikProvider value={form}>
              <InputWrapper isTextArea rows={2} key={'suggestion'} name="suggestion" />
            </FormikProvider>
          </Box>
        </Box>
      </Box>
        : <Box className='flex flex-col invest-success survey-success items-center justify-center'>
          <Box className="flex justify-center mb-4">
            <svg
              viewBox="0 0 26 26"
              xmlns="http://www.w3.org/2000/svg">
              <g
                stroke="currentColor"
                stroke-width="2"
                fill="none"
                fill-rule="evenodd"
                stroke-linecap="round"
                stroke-linejoin="round">
                <path
                  class="circle"
                  d="M13 1C6.372583 1 1 6.372583 1 13s5.372583 12 12 12 12-5.372583 12-12S19.627417 1 13 1z"
                />
                <path
                  class="tick"
                  d="M6.5 13.5L10 17 l8.808621-8.308621"
                />
              </g>
            </svg>
          </Box>
          <TextWrapper
            classList={`!font-bold !text-start !text-[26px]`}
            text={<span>
              {t('thankYouForYourTime')}
            </span>}></TextWrapper>

          <TextWrapper
            classList={`!caption !text-[14px] !text-center !max-w-[320px]`}
            text={t('weAppriciateYourFeedback')}></TextWrapper>
        </Box>}</>
    }
    actions={
      <Box className={`!w-full text-center ${!isSuccess ? '!mt-1' : ''} `}>
        {!isSuccess ? <FsButton loading={loading} disabled={!selectedPoint} onClick={() => {
          form.submitForm();
        }}>{t('submit')}</FsButton> :
          <FsButton onClick={() => {
            setOpen(false);
          }}>{t('done')}</FsButton>}
      </Box>
    }></DialogWrapper>;
};